import { buildAdjacencyList } from "../data/campusGraph";

const weightFunctions = {
  fastest: (edge) => edge.distance,

  safe: (edge) => {
    // Strongly avoid dark, isolated, non-accessible paths
    const lightingPenalty = Math.pow(10 - edge.lightingScore, 2) * 100;
    const isolationPenalty = !edge.accessibilityFriendly ? 2000 : 0;
    const crowdBonus = edge.crowdLevel * 50; // crowds = safer
    return edge.distance + lightingPenalty + isolationPenalty - crowdBonus;
  },

  calm: (edge) => {
    // Strongly avoid crowded and steep paths
    const crowdPenalty = Math.pow(edge.crowdLevel, 2) * 80;
    const elevationPenalty = Math.pow(edge.elevation, 2) * 60;
    return edge.distance + crowdPenalty + elevationPenalty;
  },

  eco: (edge) => {
    const carbonPenalty = Math.pow(edge.carbonImpact, 2) * 150;
    return edge.distance + carbonPenalty;
  },
};

export function findPath(startId, endId, mode = "fastest") {
  const adj = buildAdjacencyList();
  const weightFn = weightFunctions[mode] || weightFunctions.fastest;

  const dist = {};
  const prev = {};
  const prevEdge = {};
  const visited = new Set();

  Object.keys(adj).forEach((id) => {
    dist[id] = Infinity;
    prev[id] = null;
    prevEdge[id] = null;
  });
  dist[startId] = 0;

  const pq = [{ id: startId, cost: 0 }];

  while (pq.length > 0) {
    pq.sort((a, b) => a.cost - b.cost);
    const { id: current } = pq.shift();

    if (visited.has(current)) continue;
    visited.add(current);

    if (current === endId) break;

    for (const edgeInfo of adj[current]) {
      const neighbor = edgeInfo.neighbor;
      if (visited.has(neighbor)) continue;

      const cost = dist[current] + weightFn(edgeInfo);
      if (cost < dist[neighbor]) {
        dist[neighbor] = cost;
        prev[neighbor] = current;
        prevEdge[neighbor] = edgeInfo;
        pq.push({ id: neighbor, cost });
      }
    }
  }

  if (dist[endId] === Infinity) return null;

  const path = [];
  const pathEdges = [];
  let current = endId;
  while (current !== null) {
    path.unshift(current);
    if (prevEdge[current]) pathEdges.unshift(prevEdge[current]);
    current = prev[current];
  }

  const totalDistance = pathEdges.reduce((sum, e) => sum + e.distance, 0);
  const avgLighting = pathEdges.length > 0
    ? pathEdges.reduce((sum, e) => sum + e.lightingScore, 0) / pathEdges.length : 0;
  const avgCrowd = pathEdges.length > 0
    ? pathEdges.reduce((sum, e) => sum + e.crowdLevel, 0) / pathEdges.length : 0;

  return {
    path,
    edges: pathEdges,
    stats: {
      totalDistance: Math.round(totalDistance),
      avgLighting: avgLighting.toFixed(1),
      avgCrowd: avgCrowd.toFixed(1),
      estimatedMinutes: Math.round(totalDistance / 80),
    },
  };
}